'use client';

import { useTranslations } from 'next-intl';
import { Delete02Icon, Download01Icon, ViewIcon } from '@terra/ui-icons';
import { IconButton, MenuWithItems, Tooltip } from '@terra/ui-design-system';
import type { MenuItemProps } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { ListPageBulkActionButton } from '../../common/ListPageBulkActionButton';
import { ListPageSelectionBar } from '../../common/ListPageSelectionBar';

type MatchReplaceRulesBulkActionsBarProps = {
  selectedCount: number;
  isSubmitting: boolean;
  onClearSelection: () => void;
  onEnableSelected: () => void;
  onDisableSelected: () => void;
  onExportSelected: () => void;
  onDeleteSelected: () => void;
};

export function MatchReplaceRulesBulkActionsBar({
  selectedCount,
  isSubmitting,
  onClearSelection,
  onEnableSelected,
  onDisableSelected,
  onExportSelected,
  onDeleteSelected,
}: MatchReplaceRulesBulkActionsBarProps) {
  const tBulk = useTranslations('matchReplace.bulkActions');

  const enableDisableItems: MenuItemProps[] = [
    {
      value: 'enable',
      label: tBulk('enableSelected'),
      onClick: () => onEnableSelected(),
      disabled: isSubmitting,
    },
    {
      value: 'disable',
      label: tBulk('disableSelected'),
      onClick: () => onDisableSelected(),
      disabled: isSubmitting,
    },
  ];

  return (
    <ListPageSelectionBar
      selectedCount={selectedCount}
      clearSelectionLabel={tBulk('clearSelection')}
      onClearSelection={onClearSelection}
    >
      <Tooltip title={tBulk('enableDisableTooltip')}>
        <Box display="inline-flex" flexShrink={0}>
          <MenuWithItems
            items={enableDisableItems}
            positioning={{ strategy: 'fixed', placement: 'bottom-start' }}
            trigger={
              <IconButton
                aria-label={tBulk('enableDisableAriaLabel')}
                size="xs"
                variant="ghost"
                disabled={isSubmitting}
              >
                <ViewIcon size={12} />
              </IconButton>
            }
          />
        </Box>
      </Tooltip>
      <ListPageBulkActionButton
        label={tBulk('exportSelected')}
        tooltip={tBulk('exportSelected')}
        icon={<Download01Icon size={12} />}
        isDisabled={isSubmitting}
        onClick={onExportSelected}
      />
      <ListPageBulkActionButton
        label={tBulk('deleteSelected')}
        tooltip={tBulk('deleteSelected')}
        icon={<Delete02Icon size={12} />}
        isDisabled={isSubmitting}
        onClick={onDeleteSelected}
      />
    </ListPageSelectionBar>
  );
}
